/**
 * Typed abbreviations for the BACKEND statements.
 *
 * The matcher can already reach a statement through its key tail, which is why
 * `lafb` and `sveb` work without a list: their keys are literally
 * `ivConduction.LAFB` and `heartRhythm.SVEB`. But the tail has to START with
 * what is typed, and most backend keys do not read that way —
 * `ivConduction.completeRBBB` has the tail `completerbbb`, so typing the code
 * everyone actually uses, `rbbb`, matched NOTHING AT ALL. The same held for
 * `lbbb`, `irbbb`, `vt`, `svt`, `nsr` and the rest.
 *
 * This file closes that gap. It maps a backend statement's key to the codes a
 * technician types, and `index.ts` attaches them when the catalogue is
 * assembled.
 *
 * It deliberately lives OUTSIDE `catalogue.generated.ts`, which is overwritten
 * on every run of the generator, and outside `catalogue.custom.ts`, which is
 * for statements the backend has no wording for. Nothing here changes a single
 * character of backend text — these are only ways to reach it.
 *
 * Codes avoided on purpose:
 *
 *   `st`  — the first-word rule already makes it find every ST statement, and
 *           claiming it for sinus tachycardia would bury them.
 *   `q`   — below the two-character minimum.
 */
export const BACKEND_SHORTCUTS: {[key: string]: string[]} = {
    // ---- rhythm ----
    "heartRhythm.normalSinusRhythm": ["nsr"],
    "heartRhythm.bradySinusRhythm": ["sb", "brady"],
    "heartRhythm.tachySinusRhythm": ["stach", "tachy"],
    "heartRhythm.aFibrillation": ["af", "afib"],
    "heartRhythm.aFlutter": ["afl", "flutter"],
    "heartRhythm.VEB": ["veb", "pvc"],
    "heartRhythm.SVEB": ["pac"],
    "heartRhythm.vTachy": ["vt"],
    "heartRhythm.svTachy": ["svt"],
    "heartRhythm.vBigeminy": ["vbig"],
    "heartRhythm.svBigeminy": ["svbig"],
    "heartRhythm.vTrigeminy": ["vtrig"],
    "heartRhythm.svTrigeminy": ["svtrig"],
    "heartRhythm.vCouple": ["vcouplet"],
    "heartRhythm.svCouple": ["svcouplet"],
    "heartRhythm.vTriplet": ["vtriplet"],
    "heartRhythm.svTriplet": ["svtriplet"],
    "heartRhythm.junctional": ["junc"],
    "heartRhythm.wanderingPM": ["wap"],
    "heartRhythm.ectopicPWave": ["ectopicp"],
    "heartRhythm.unclassified": ["unclass"],

    // ---- atrioventricular conduction ----
    "avConduction.firstAVBlock": ["avb1", "1avb"],
    "avConduction.secondAVBlock": ["avb2", "2avb"],
    "avConduction.thirdAVBlock": ["avb3", "3avb", "chb"],
    "avConduction.shortPR": ["shortpr"],

    // ---- atrial enlargement ----
    mitralP: ["lae"],
    pulmonaryP: ["rae"],

    // ---- Q waves ----
    qWaveInLead: ["qw"],

    // ---- intraventricular conduction ----
    // the gap that prompted this file
    "ivConduction.completeRBBB": ["rbbb", "crbbb"],
    "ivConduction.incompleteRBBB": ["irbbb"],
    "ivConduction.completeLBBB": ["lbbb", "clbbb"],
    "ivConduction.LPFB": ["lpfb", "lph"],
    // `lafb` reaches the definite one through its key tail, but the tail of
    // `possibleLAFB` does not start with it, so the hedged forms need their own
    "ivConduction.possibleLAFB": ["plafb"],
    "ivConduction.possibleLPFB": ["plpfb"],
    "ivConduction.bifascicularBlock": ["bifasc"],

    // ---- repolarisation ----
    "repolarization.stElevation": ["ste"],
    "repolarization.stDepression": ["std"],
    "repolarization.aspecificStDepression": ["astd"],
    "repolarization.nonSignificantStElevation": ["nsste"],
    "repolarization.invertedTWave": ["twi", "tinv"],
    "repolarization.brugada": ["brugada"],

    // ---- technical ----
    "conclusion.noisyLeads": ["noisy"],
};
